"use client";

import type { ComponentProps, MouseEvent } from "react";

import {
  buildSdkHref,
  isInternalSdkHref,
  shouldOpenSdkHrefInNewTab,
} from "@/lib/navigation";
import { useSdk } from "@/provider";

export interface NavLinkProps extends ComponentProps<"a"> {
  testId?: string;
  customLink?: boolean;
}

export const NavLink = ({
  children,
  customLink,
  testId,
  href,
  onClick,
  rel: providedRel,
  target: providedTarget,
  ...rest
}: NavLinkProps) => {
  const sdk = useSdk();

  if (!href) {
    throw new Error("NavLink requires href");
  }

  const shouldOpenInNewTab = shouldOpenSdkHrefInNewTab(
    href,
    sdk.config._usage,
    customLink
  );
  const nextHref = buildSdkHref(href, sdk.config._usage, customLink);
  const target = providedTarget ?? (shouldOpenInNewTab ? "_blank" : undefined);
  const rel =
    providedRel ?? (target === "_blank" ? "noreferrer nofollow" : undefined);
  const shouldIntercept =
    sdk.config._usage === "internal" &&
    (!target || target === "_self") &&
    isInternalSdkHref(nextHref);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (event.defaultPrevented) {
      return;
    }

    if (shouldIntercept) {
      event.preventDefault();
    }

    sdk.events.emit("linkClick", {
      href: nextHref,
      target,
    });
  };

  return (
    <a
      data-testid={testId}
      href={nextHref}
      onClick={handleClick}
      rel={rel}
      target={target}
      {...rest}
    >
      {children}
    </a>
  );
};
